import React, { useState } from 'react';
import { V2Trade, V2TradeChange, previewTradePatch, applyTradePatch } from '../../services/optionsTraderV2Service';

const RULES: { key: string; label: string; widthOnly?: boolean }[] = [
  { key: 'profitTargetPct', label: 'Profit target (%)' }, { key: 'stopLossMultiplier', label: 'Stop loss (x credit)' },
  { key: 'stopLossMinDte', label: 'Stop loss from (days left)' }, { key: 'preExpiryBufferPts', label: 'Expiry-day buffer (points)' },
  { key: 'hardStopMultiplier', label: 'Hard stop (x credit)', widthOnly: true },
];
const CHANGE_LABEL: Record<string, string> = {
  management: 'Management', alertsMuted: 'Alerts muted', entryCredit: 'Entry price', exitRules: 'Rules', split: 'Split into single legs',
  profitTargetPct: 'profit target (%)', stopLossMultiplier: 'stop loss (x credit)', stopLossMinDte: 'stop loss from (days left)',
  preExpiryBufferPts: 'expiry-day buffer (points)', hardStopMultiplier: 'hard stop (x credit)',
};
const humanize = (k: string) => CHANGE_LABEL[k] || k.replace(/([A-Z])/g, ' $1').replace(/_/g, ' ').toLowerCase().replace(/^./, c => c.toUpperCase());
const fmt = (k: string, v: any): string => {
  if (v === null || v === undefined || v === '') return 'default';
  if (k === 'management') return v === 'managed' ? 'managed' : 'alerts only';
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'object') return Object.entries(v).map(([a, b]) => `${humanize(a).toLowerCase()} ${fmt(a, b)}`).join(', ');
  return String(v);
};
/** Preview text from the API's "after" values (display only; no math here). */
const describeAfter = (after: Record<string, any> | undefined) =>
  Object.entries(after || {}).map(([k, v]) => `${humanize(k)}: ${fmt(k, v)}.`).join(' ');

/** Row actions for a manual position (SP4 §7, §9). Every change is Preview -> Confirm. */
const ManualTradeActions: React.FC<{ token: string; trade: V2Trade; onChanged: (t: V2Trade, all?: V2Trade[]) => void }> = ({ token, trade, onChanged }) => {
  const [panel, setPanel] = useState<'rules' | 'entry' | null>(null);
  const [rules, setRules] = useState<Record<string, string>>({});
  const [entry, setEntry] = useState(''); const [effect, setEffect] = useState<'credit' | 'debit'>('credit');
  const [pending, setPending] = useState<{ planId: string; text: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const open = trade.status === 'open';
  const covered = trade.strategy === 'covered_call'; // D10: alerts only in SP4 (API refuses managed: COVERED_CALL_ALERTS_ONLY)
  // Same reasons and wording as the API (tradePatch.js), so the disabled button says why.
  const managedReason: string | null = covered ? 'Covered call (alerts only)'
    : (trade.entryUnknown || typeof trade.entryCredit !== 'number') ? 'Set the entry price first: rules need it'
    : trade.entryCredit <= 0 ? 'Debit positions are alerts only for now'
    : trade.ratioLegs ? 'Positions with uneven (ratio) legs are alerts only'
    : trade.structureBroken ? 'Part of this position changed in your broker account; split it first' : null;
  const preview = async (change: V2TradeChange) => {
    setErr(null);
    try {
      const p = await previewTradePatch(token, trade.tradeId, change);
      setPending({ planId: p.planId, text: [describeAfter(p.after), ...(p.warnings || [])].filter(Boolean).join(' ') });
    } catch (e: any) { setErr(e?.message || 'Preview failed'); }
  };
  const confirm = async () => {
    if (!pending) return;
    try { const r = await applyTradePatch(token, trade.tradeId, pending.planId); (r.trades && r.trades.length ? onChanged(r.trade, r.trades) : onChanged(r.trade)); setPending(null); setPanel(null); }
    catch (e: any) { setErr(e?.message || 'Could not save the change'); }
  };
  if (!open) return null;
  return (
    <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6, fontSize: 12 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        {trade.management === 'managed'
          ? <button onClick={() => preview({ management: 'alerts' })}>Switch to alerts only</button>
          : <button onClick={() => preview({ management: 'managed' })} disabled={!!managedReason} title={managedReason || undefined}>Switch to managed</button>}
        <button onClick={() => setPanel(panel === 'rules' ? null : 'rules')}>Edit rules</button>
        <button onClick={() => preview({ alertsMuted: !trade.alertsMuted })}>{trade.alertsMuted ? 'Unmute alerts' : 'Mute alerts'}</button>
        <button onClick={() => setPanel(panel === 'entry' ? null : 'entry')}>Set entry price</button>
        {(trade.legs || []).length > 1 && <button onClick={() => preview({ split: true })}>Split into single legs</button>}
      </div>
      {managedReason && trade.management !== 'managed' && !covered && !trade.entryUnknown && <span style={{ color: '#fbbf24' }}>{managedReason}</span>}
      {covered && <span style={{ color: '#a78bfa' }}>Covered call (alerts only)</span>}
      {covered && trade.coverageLost && <span style={{ color: '#f87171' }}>No longer fully covered by shares</span>}
      {trade.entryUnknown && <span style={{ color: '#fbbf24' }}>Set the entry price to turn on rules</span>}
      {panel === 'rules' && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {RULES.filter(r => !r.widthOnly || (trade.spreadWidth ?? 0) > 0).map(r => (
            <label key={r.key}>{r.label} <input aria-label={r.label} style={{ width: 60 }} value={rules[r.key] ?? String((trade.exitRules as any)?.[r.key] ?? '')} onChange={ev => setRules({ ...rules, [r.key]: ev.target.value })} /></label>
          ))}
          <button onClick={() => preview({ exitRules: Object.fromEntries(Object.entries(rules).map(([k, v]) => [k, v === '' ? null : Number(v)])) })}>Preview</button>
        </div>
      )}
      {panel === 'entry' && (
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <label>Entry price per contract <input aria-label="Entry price per contract" style={{ width: 70 }} value={entry} onChange={ev => setEntry(ev.target.value)} /></label>
          <label><input type="radio" aria-label="Credit received" checked={effect === 'credit'} onChange={() => setEffect('credit')} />Credit received</label>
          <label><input type="radio" aria-label="Debit paid" checked={effect === 'debit'} onChange={() => setEffect('debit')} />Debit paid</label>
          <button onClick={() => preview({ entryPrice: (effect === 'credit' ? 1 : -1) * Math.abs(Number(entry)) })} disabled={!(Number(entry) > 0)}>Preview</button>
        </div>
      )}
      {pending && <div>{pending.text} <button onClick={confirm}>Confirm</button> <button onClick={() => setPending(null)}>Cancel</button></div>}
      {err && <span style={{ color: '#f87171' }}>{err}</span>}
    </div>
  );
};
export default ManualTradeActions;
